import { OrderStatus } from '@utility/constants/common';

/* A batch is picked and packed by hand at one warehouse: every rule follows. */

export type BatchProvider = {
  _id?: string;
  integration_type?: string;
  name?: string;
};

export type BatchCandidate = {
  _id: string;
  state?: string;
  selectedFP?: BatchProvider;
  fulfillment_providers?: BatchProvider[];
  is_fba?: boolean;
  is_deliverr?: boolean;
};

/** The chosen provider, or the first one the backend offered. */
export function getOrderProvider(
  order: BatchCandidate,
): BatchProvider | undefined {
  return order.selectedFP?._id
    ? order.selectedFP
    : order.fulfillment_providers?.[0];
}

/** Whether an order could join any batch at all. */
export function canBatchOrder(order: BatchCandidate): boolean {
  if (order.is_fba || order.is_deliverr) {
    return false;
  }

  if (
    order.state !== OrderStatus.PendingShipment &&
    order.state !== OrderStatus.Completed
  ) {
    return false;
  }

  return getOrderProvider(order)?.integration_type === 'manual';
}

/**
 * Whether an order fits the batch being collected. `providerId` is the
 * warehouse the first pick settled on; without one, nothing is settled yet.
 */
export function canBatchWith(
  order: BatchCandidate,
  providerId: string | undefined,
): boolean {
  if (!canBatchOrder(order)) {
    return false;
  }

  return !providerId || getOrderProvider(order)?._id === providerId;
}

/** Whether the page holds two batchable orders from the same warehouse. */
export function canStartBatching(orders: BatchCandidate[]): boolean {
  const perProvider = new Map<string, number>();

  for (const order of orders) {
    if (!canBatchOrder(order)) {
      continue;
    }

    const providerId = getOrderProvider(order)?._id;

    if (providerId) {
      const seen = (perProvider.get(providerId) || 0) + 1;

      if (seen > 1) {
        return true;
      }

      perProvider.set(providerId, seen);
    }
  }

  return false;
}

export type OpenBatch = {
  _id: string;
  name: string;
  completed?: boolean;
  fulfillment_provider_id?: { _id?: string };
};

/** The open batches a selection could be added to: same warehouse, unfinished. */
export function getBatchTargets(
  batches: OpenBatch[] | undefined,
  providerId: string | undefined,
): { id: string; name: string }[] {
  if (!providerId) {
    return [];
  }

  return (batches || [])
    .filter(
      batch =>
        !batch.completed && batch.fulfillment_provider_id?._id === providerId,
    )
    .map(batch => ({ id: batch._id, name: batch.name }));
}
